import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

/**
 * IrrigationStats
 *
 * Compact dashboard of irrigation metrics: WUE, water saved,
 * irrigation network km, system type, and monthly water usage trend.
 */

const GaugeRing = ({ value, max = 100, size = 72, strokeWidth = 6, color = "#059669", label, unit, delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(value / max, 1);

  return (
    <div ref={ref} className="flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={inView ? { strokeDashoffset: circumference * (1 - pct) } : {}}
          transition={{ duration: 1.2, delay, ease: "easeOut" }}
        />
      </svg>
      <p className="text-lg font-bold text-slate-900 -mt-[calc(50%+10px)] mb-0">{typeof value === "number" ? value : value}{unit}</p>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-7 mb-0">{label}</p>
    </div>
  );
};

const MiniTrend = ({ data, color = "#059669", height = 40 }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  if (!data || data.length < 2) return null;

  const max = Math.max(...data.map((d) => d.value));
  const min = Math.min(...data.map((d) => d.value));
  const range = max - min || 1;
  const w = 200;

  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * w,
    y: height - ((d.value - min) / range) * (height - 8) - 4,
  }));

  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = `${d} L ${w} ${height} L 0 ${height} Z`;

  return (
    <svg ref={ref} viewBox={`0 0 ${w} ${height}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <path d={areaD} fill={color} opacity={0.08} />
      {inView && (
        <motion.path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, ease: "easeInOut" }}
        />
      )}
    </svg>
  );
};

const IrrigationStats = ({ irrigation, isArabic }) => {
  if (!irrigation) return null;

  const metrics = [
    {
      value: irrigation.wue || 1.0,
      max: 2.0,
      unit: "x",
      label: isArabic ? "كفاءة المياه" : "WUE",
      color: "#059669",
    },
    {
      value: parseFloat(String(irrigation.waterSaved).replace("%", "")) || 0,
      max: 100,
      unit: "%",
      label: isArabic ? "توفير المياه" : "Water Saved",
      color: "#2563eb",
    },
  ];

  const infoCards = [
    {
      icon: "🔧",
      label: isArabic ? "نوع النظام" : "System Type",
      value: irrigation.systemType || (isArabic ? "تنقيط" : "Drip Irrigation"),
    },
    {
      icon: "📏",
      label: isArabic ? "شبكة الري" : "Network",
      value: `${irrigation.networkKm || 0} km`,
    },
    {
      icon: "💧",
      label: isArabic ? "م³ / فدان / شهر" : "m³/acre/month",
      value: irrigation.avgConsumption || "—",
    },
    {
      icon: "⚡",
      label: isArabic ? "مصدر الضخ" : "Pump Source",
      value: irrigation.pumpSource || (isArabic ? "شمسي" : "Solar"),
    },
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
      <div className={`flex items-center gap-2 mb-5 ${isArabic ? "flex-row-reverse" : ""}`}>
        <span className="text-lg">💧</span>
        <h3 className="text-base font-bold text-slate-900 mb-0">
          {isArabic ? "إحصائيات الري" : "Irrigation Statistics"}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gauge rings */}
        <div className="flex items-center justify-around py-4">
          {metrics.map((m, i) => (
            <GaugeRing key={m.label} {...m} delay={i * 0.2} />
          ))}
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-2 gap-3">
          {infoCards.map((card) => (
            <div key={card.label} className="rounded-xl bg-slate-50 p-3 border border-slate-100">
              <div className={`flex items-center gap-1.5 mb-1 ${isArabic ? "flex-row-reverse" : ""}`}>
                <span className="text-sm">{card.icon}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{card.label}</span>
              </div>
              <p className="text-sm font-bold text-slate-900 mb-0">{card.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly usage trend */}
      {irrigation.monthlyUsage && irrigation.monthlyUsage.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <p className={`text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 ${isArabic ? "text-right" : ""}`}>
            {isArabic ? "استهلاك المياه الشهري (م³)" : "Monthly Water Consumption (m³)"}
          </p>
          <MiniTrend data={irrigation.monthlyUsage} color="#2563eb" height={48} />
          <div className={`flex justify-between text-[10px] text-slate-400 mt-1 ${isArabic ? "flex-row-reverse" : ""}`}>
            <span>{irrigation.monthlyUsage[0]?.month}</span>
            <span>{irrigation.monthlyUsage[irrigation.monthlyUsage.length - 1]?.month}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default IrrigationStats;
